// "Import with suggested fixes" (bos-app#213, US-10, AC-08), pure: what its
// summary dialog says, from the plan the store applied and the check the
// step ran with `importDrafts`, and the dialog's states.
import type { CatalogImportResult } from '@/api/models';
import {
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import type {
    AllFixesPlan,
    CatalogImportState,
    ParsedLine,
} from '@/pages/catalogImport/store';
import {
    FIXABLE_ISSUE_KINDS,
    ISSUE_KIND_FIX,
    issueEntriesOf,
} from './importIssues';
import type { FixableIssueKind } from './importIssues';
import type { ImportSubmitState } from './importSubmit';

/**
 * The kinds "What we changed" can list: those whose default it applied, and
 * the extra barcodes the import keeps. The kinds with no default change
 * nothing; their rows are the drafts.
 */
export type SuggestedFixKind = FixableIssueKind | 'alternates';

const SUGGESTED_FIX_KINDS: readonly SuggestedFixKind[] = [
    ...FIXABLE_ISSUE_KINDS,
    'alternates',
];

/**
 * One line of "What we changed": a kind and how many it covers, in the
 * kind's own unit (IMEIs, variants, SKUs, shared barcodes, or variants
 * that keep their extra barcodes), and the rows its default changed.
 */
export type SuggestedFix = {
    kind: SuggestedFixKind;
    count: number;
    /**
     * The rows the default changed (the plan's `counts`, bos-app#213: "rows
     * changed per fix"); `null` for the extra barcodes, which the import
     * keeps on its own without changing a row.
     */
    changedRows: number | null;
};

/** What the dialog says before anything is written. */
export type SuggestedFixesSummary = {
    /** The kinds the defaults changed, in `ISSUE_KINDS` order; none at 0. */
    fixes: SuggestedFix[];
    /** Rows the check answered `draft`, not excluded: imported as drafts. */
    drafts: number;
    /** Rows the import writes: ready and draft, not excluded. */
    rows: number;
    /**
     * Rows left out because a number cell could not be read
     * (`unreadableRowsOf`): the server takes numbers only, so a draft would
     * lose the value. Sent excluded; the user's exclusions are not here.
     */
    leftOut: number;
    /**
     * Why nothing can be imported (the check's header problems: the branch,
     * accounting not ready, too many units), one sentence each; empty when
     * the import can go.
     */
    refusals: CatalogImportNotice[];
};

/**
 * The dialog: closed; the defaults applied and the check on its way
 * (`applying`); the check did not answer, so the defaults were taken back
 * (`failed`, Try again); or its summary (`ready`), with what the import
 * said when it was confirmed and did not go through (`submit`).
 */
export type SuggestedFixesState =
    | { status: 'closed' }
    | { status: 'applying' }
    | { status: 'failed' }
    | {
          status: 'ready';
          summary: SuggestedFixesSummary;
          submit: ImportSubmitState;
      };

export const SUGGESTED_FIXES_CLOSED: SuggestedFixesState = { status: 'closed' };

/**
 * The rows "Import with suggested fixes" leaves out, by number: those with a
 * number cell the parse could not read (`unreadable`), not excluded. The
 * line leaves such a field off, so the server would import the row without
 * the value (wholesale price, minimum quantity) or make it a draft that
 * says the cell was empty; the store keeps them needing attention
 * (`withUnreadableCells`). The import sends them excluded and the summary
 * counts them (`leftOut`), as rows the import cannot take.
 */
export const unreadableRowsOf = (
    lines: readonly ParsedLine[],
    exclusions: CatalogImportState['exclusions'],
): number[] =>
    lines
        .filter(
            (line) =>
                line.unreadable !== undefined &&
                Object.keys(line.unreadable).length > 0 &&
                !exclusions.has(line.rowNumber),
        )
        .map((line) => line.rowNumber);

export type SuggestedFixesSummarySource = Pick<
    CatalogImportState,
    'exclusions'
> & {
    /** The plan the store applied (`applyAllFixes`). */
    plan: Pick<AllFixesPlan, 'groups' | 'counts'>;
    /** The check's answer, sent with `importDrafts`. */
    answer: CatalogImportResult;
    /** The lines it answered: the plan's. */
    lines: ParsedLine[];
};

/**
 * What an answer with `importDrafts` says the import writes: the rows it
 * imports as drafts and in all (excluded rows in neither), and its header
 * problems. A refused import's fresh answer updates the summary with it.
 */
export const answerFiguresOf = (
    answer: CatalogImportResult,
    exclusions: CatalogImportState['exclusions'],
): Omit<SuggestedFixesSummary, 'fixes' | 'leftOut'> => {
    let drafts = 0;
    let rows = 0;
    for (const line of answer.lines) {
        if (exclusions.has(line.rowNumber)) {
            continue;
        }
        if (line.status === 'draft') {
            drafts += 1;
            rows += 1;
        } else if (line.status === 'ready') {
            rows += 1;
        }
    }
    return {
        drafts,
        rows,
        refusals: answer.header.problems.map((problem) => noticeFor(problem)),
    };
};

/**
 * The summary from the real answer: each default by the groups it changed
 * (the plan's `groups`) and the rows (its `counts`), the extra barcodes the import keeps (the variants
 * the answer warns `BARCODES_KEPT_AS_ALTERNATES` on, as the issues summary
 * counts them), the rows the answer imports as drafts and in all, the rows
 * left out for an unreadable number cell (`unreadableRowsOf`), and the
 * header problems that refuse the import. Rows the user excluded count in
 * none of them.
 */
export const suggestedFixesSummaryOf = ({
    plan,
    answer,
    lines,
    exclusions,
}: SuggestedFixesSummarySource): SuggestedFixesSummary => {
    const alternates =
        issueEntriesOf({ lines, preview: answer, exclusions }).find(
            (entry) => entry.kind === 'alternates',
        )?.count ?? 0;
    const fixes = SUGGESTED_FIX_KINDS.map((kind): SuggestedFix => {
        if (kind === 'alternates') {
            return { kind, count: alternates, changedRows: null };
        }
        const code = ISSUE_KIND_FIX[kind];
        return {
            kind,
            count: plan.groups[code],
            changedRows: plan.counts[code],
        };
    }).filter((fix) => fix.count > 0);

    return {
        fixes,
        leftOut: unreadableRowsOf(lines, exclusions).length,
        ...answerFiguresOf(answer, exclusions),
    };
};

/** The import can be confirmed: nothing refuses it and it has rows to write. */
export const canConfirmSuggestedFixes = (
    summary: SuggestedFixesSummary,
): boolean => summary.refusals.length === 0 && summary.rows > 0;
